/**
 * The help center's pages (web-app.md WA-6), in reading order and grouped by topic for the
 * navigation. Their text is `helpContent.ts`'s, loaded with the help itself.
 */
export const HELP_GROUPS = [
  { id: "start", pages: ["start", "projects", "connectors", "agents"] },
  { id: "documents", pages: ["documents", "import"] },
  { id: "work", pages: ["threads", "reviews", "memories", "workflows", "batches"] },
  { id: "dataModel", pages: ["data-model", "data"] },
  { id: "settings", pages: ["profile", "github", "project-settings", "administration", "server-keys"] },
  { id: "reference", pages: ["glossary"] },
] as const;

export type HelpGroupId = (typeof HELP_GROUPS)[number]["id"];
export type HelpPageId = (typeof HELP_GROUPS)[number]["pages"][number];

export const HELP_PAGE_IDS: readonly HelpPageId[] = HELP_GROUPS.flatMap((group) => group.pages);

/** The page the help opens at. */
export const HELP_START_PAGE: HelpPageId = "start";

export function isHelpPageId(value: string | undefined): value is HelpPageId {
  return HELP_PAGE_IDS.includes(value as HelpPageId);
}

export function helpGroupOf(page: HelpPageId): HelpGroupId {
  const group = HELP_GROUPS.find((candidate) => (candidate.pages as readonly HelpPageId[]).includes(page));
  if (group === undefined) throw new Error(`Help page ${page} is in no group.`);
  return group.id;
}

/** The pages before and after `page` in reading order. */
export function helpNeighbours(page: HelpPageId): { previous: HelpPageId | null; next: HelpPageId | null } {
  const index = HELP_PAGE_IDS.indexOf(page);
  return { previous: HELP_PAGE_IDS[index - 1] ?? null, next: HELP_PAGE_IDS[index + 1] ?? null };
}
